import type { SignatorySelection } from './CompanySignatoryBlock';
import { normalizeSignerRole } from './ContractSignPage.helpers';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface SignatoryBlockRef {
    companyName: string;
    companyCnpj: string;
    participantSource: 'ASSIGNOR' | 'RECIPIENT';
}

export const blockKeyOf = (block: SignatoryBlockRef): string =>
    `${block.participantSource}-${block.companyCnpj.replace(/\D/g, '')}`;

/** E-mail deste envio: o editado na seleção ou, sem edição, o do cadastro (AF-4138). */
export const resolveSignerEmail = (selection: SignatorySelection): string =>
    (selection.emailOverride ?? selection.signatory.email ?? '').trim();

/** E-mail aceito para o envio, depois de aparar os espaços. */
export const isValidSignerEmail = (value: string): boolean => EMAIL_PATTERN.test(value.trim());

/**
 * Signatários selecionados nos quadrados, no formato do PUT /contracts/{id} e do
 * validate-for-signature (contrato: services/contracts/validate-for-signature-request.schema.json).
 * Cada um leva o lado do quadrado de onde foi escolhido, que define a ordem de assinatura.
 */
export const buildSignersPayload = (
    blocks: SignatoryBlockRef[],
    selections: Record<string, SignatorySelection[]>,
): Record<string, unknown>[] => blocks.flatMap(block => {
    const selectedInBlock = (selections[blockKeyOf(block)] || []).filter(s => s.selected);
    return selectedInBlock.map((sel, idx) => ({
        // When the PDF name diverges from the Person record, the operator chooses which one goes
        // to the provider. Defaults to the Person record.
        name: sel.nameSource === 'document' && sel.pdfName ? sel.pdfName : sel.signatory.fullName,
        email: resolveSignerEmail(sel),
        role: normalizeSignerRole(sel.contractRole),
        govTaxId: (sel.signatory.socialId || '').replace(/\D/g, ''),
        participant_source: block.participantSource,
        anchor_name: block.companyName,
        anchor_index: idx,
        anchor_total: selectedInBlock.length,
        person_verified_at: sel.signatory.lastVerifiedAt || undefined,
        use_digital_certificate: sel.useDigitalCertificate || false,
    }));
});
